import React from "react";
import { countryCodeToCountryName } from "~/utils";
import { getAddressCity, getAddressCountryCode, getAddressEmail, getAddressFullName, getAddressPhone, getAddressStreet, getAddressZipCode } from "~/cores/addressCore";
import { TurnrButton } from "../../buttons/TurnrButton";
import { TurnrGrid } from "../../TurnrGrid.jsx";
import { TurnrModalContainer, TurnrModalContent } from "../../modals/TurnrModal.jsx";
import CountryFlag from "../../CountryFlag.jsx";
import { COLORS } from "rylak-common/src/constants";
import { TurnrTableCell, TurnrTableDropdownCell } from "../TurnrTableSmart.jsx";
import { customerHasStatistics, getCustomerEmail, getCustomerFullName, getCustomerPhone } from "~/cores/customerCore";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import { getTurnrCustomerHref } from "~/cores/pageAccessCore";
import HighlightText, { shouldHighlightText } from "../../highlightText/HighlightText.jsx";
import ReturnStatisticsCard from "../../charts/chartCommon/tooltips/ReturnStatisticsCard.jsx";
import { getNameAbbreviation } from "rylak-common";
import CustomerFlagChips from "~/components/chips/CustomerFlagChips.jsx";
import CustomerTagsChips from "~/components/chips/CustomerTagsChips.jsx";

export function getTableHeadCustomer(props) {
    return {
        id: "order.entityAddress.fullName",
        label: "Customer",
        sortable: true,
        ...props,
    };
}

function DropdownContent({ e, c, name, email, phone, highlightText }) {
    const countryCode = getAddressCountryCode(e);
    const city = getAddressCity(e);

    const countryName = countryCodeToCountryName(countryCode);
    const street = getAddressStreet(e);
    const zipcode = getAddressZipCode(e);
    return (
        <TurnrModalContainer>
            <TurnrModalContent
                size={"small"}
                style={{
                    whiteSpace: "nowrap",
                    width: "min-content",
                }}
            >
                {countryCode && (
                    <div
                        style={{
                            width: "20px",
                            position: "absolute",
                            right: 10,
                            top: 10,
                        }}
                    >
                        <CountryFlag countryCode={countryCode} />
                    </div>
                )}
                <TurnrGrid container style={{ fontSize: "12px" }} spacing={2}>
                    <TurnrGrid item xs={12} style={{ marginRight: "24px" }}>
                        <div
                            style={{
                                fontSize: "13px",
                                marginBottom: "3px",
                            }}
                        >
                            <HighlightText highlightText={highlightText} text={name} />
                        </div>
                        <div
                            style={{
                                color: COLORS.WEAK_TEXT,
                                marginBottom: "1px",
                            }}
                        >
                            {city && (
                                <>
                                    <HighlightText highlightText={highlightText} text={city} />,{" "}
                                </>
                            )}
                            {countryName}
                        </div>

                        <div
                            style={{
                                color: COLORS.WEAK_TEXT,
                            }}
                        >
                            {street && (
                                <>
                                    <HighlightText highlightText={highlightText} text={street} />,{" "}
                                </>
                            )}
                            <HighlightText highlightText={highlightText} text={zipcode} />
                        </div>
                    </TurnrGrid>

                    <TurnrGrid item xs={12} style={{ color: COLORS.WEAK_TEXT }}>
                        <div style={{ marginBottom: "3px" }}>{<HighlightText highlightText={highlightText} text={email} />}</div>
                        <div>
                            <HighlightText highlightText={highlightText} text={phone} />
                        </div>
                    </TurnrGrid>
                </TurnrGrid>
            </TurnrModalContent>
            {customerHasStatistics(c) && (
                <>
                    <LineDivider />

                    <TurnrModalContent size={"small"}>
                        <ReturnStatisticsCard customer={c} showValue={false} />
                    </TurnrModalContent>
                </>
            )}

            <LineDivider />
            {c && (
                <TurnrModalContent size={"small"}>
                    <TurnrButton href={getTurnrCustomerHref({ customer: c })} size={"small"} fullWidth variant={"outlined"}>
                        View customer
                    </TurnrButton>
                </TurnrModalContent>
            )}
        </TurnrModalContainer>
    );
}

export function TableCellCustomer({ order, highlightText, customer, entityAddress, fontSize = "12px", nameInitials, ...rest }) {
    const e = entityAddress || order?.entityAddress || customer?.latestAddress || order?.address;
    const c = customer || order?.customer;
    // We prefer to go by entityAddress which contains more info

    const name = getCustomerFullName(c) || getAddressFullName(e);
    const email = getAddressEmail(e) || getCustomerEmail(c);
    const phone = getAddressPhone(e) || getCustomerPhone(c);

    const countryCode = getAddressCountryCode(e);
    const city = getAddressCity(e);

    let nameOrEmailOrInitials = name || email;

    if (nameInitials) {
        nameOrEmailOrInitials = getNameAbbreviation({ name: nameOrEmailOrInitials });
    }

    const matchEntireCustomer =
        !shouldHighlightText(nameOrEmailOrInitials, highlightText) &&
        (shouldHighlightText(name, highlightText) || shouldHighlightText(email, highlightText) || shouldHighlightText(phone, highlightText) || shouldHighlightText(city, highlightText));

    if (!c && !e) {
        return <TurnrTableCell {...rest} />;
    }
    return (
        <TurnrTableDropdownCell {...rest} tight={true} dropdownContent={<DropdownContent e={e} c={c} name={name} email={email} phone={phone} highlightText={highlightText} />}>
            <div
                style={{
                    fontSize: fontSize,
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    color: COLORS.MID_BROWN,
                }}
            >
                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    {countryCode && <CountryFlag countryCode={countryCode} width={16} />}
                    <div
                        style={{
                            whiteSpace: nameOrEmailOrInitials?.length > 35 && "normal",
                            wordBreak: "break-word",
                        }}
                    >
                        <HighlightText highlightText={matchEntireCustomer ? nameOrEmailOrInitials : highlightText} text={nameOrEmailOrInitials} />
                    </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                    <CustomerFlagChips customer={c} />
                    <CustomerTagsChips customer={c} />
                </div>
            </div>
        </TurnrTableDropdownCell>
    );
}
